import React from "react";
import { Instagram, Facebook, Linkedin, MapPin, Phone, Mail } from "lucide-react";

// Simple inline TikTok icon (lucide doesn't ship one)
function TikTokIcon({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.5 7.9a6.9 6.9 0 01-4.1-1.4v8.9a5.7 5.7 0 11-5.7-5.7c.3 0 .6 0 .9.1v2.9a2.7 2.7 0 102 2.6V2h2.8A4.1 4.1 0 0018 5.2a4 4 0 002.5 1V7.9z"/>
    </svg>
  );
}

export default function Footer() {
  return (
    <footer id="contact" className="bg-forest text-cream/90 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <img
            src="/logos/mark_organic.png"
            alt="Dr Du Pré Louw"
            className="w-14 h-14 object-contain bg-cream/95 rounded-full p-1"
          />
          <div className="font-display text-2xl text-cream mt-4">Dr Du Pré Louw</div>
          <div className="eyebrow text-clay mt-1">Family Practice · Bellville</div>
          <p className="text-cream/70 mt-4 text-sm leading-relaxed">
            Warm, trusted medical care for your whole family. Walk-ins welcome and most medical aids accepted at medical-aid rates.
          </p>
          <div className="flex gap-3 mt-6">
            <a href="https://www.instagram.com/dlouwmd/" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-cream/10 hover:bg-clay transition-colors flex items-center justify-center" data-testid="social-instagram">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://www.facebook.com/doctorduprelouw/" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-cream/10 hover:bg-clay transition-colors flex items-center justify-center" data-testid="social-facebook">
              <Facebook className="w-4 h-4" />
            </a>
            <a href="https://www.tiktok.com/@du.pre.louw" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-cream/10 hover:bg-clay transition-colors flex items-center justify-center" data-testid="social-tiktok">
              <TikTokIcon className="w-4 h-4" />
            </a>
            <a href="https://www.linkedin.com/in/dr-du-pr%C3%A9-louw-55019325/" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-cream/10 hover:bg-clay transition-colors flex items-center justify-center" data-testid="social-linkedin">
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <div className="eyebrow text-sage">Visit us</div>
          <div className="mt-3 space-y-2 text-sm">
            <p className="flex gap-2"><MapPin className="w-4 h-4 shrink-0 mt-0.5"/> Oakmed Medical Centre<br/>50 John X Merriman, Oakdale, Bellville, 7530</p>
            <p className="text-cream/70">Mon – Fri · 8am – 5pm</p>
            <a href="https://www.google.com/maps/search/?api=1&query=Oakmed+Medical+Centre+Bellville" target="_blank" rel="noreferrer" className="text-clay underline underline-offset-4" data-testid="footer-map-link">View on Google Maps</a>
          </div>
        </div>

        <div>
          <div className="eyebrow text-sage">Call or message</div>
          <div className="mt-3 space-y-2 text-sm">
            <a href="tel:0212240204" className="flex gap-2 items-center hover:text-clay" data-testid="footer-phone-1"><Phone className="w-4 h-4"/> 021 224 0204</a>
            <a href="tel:0210233319" className="flex gap-2 items-center hover:text-clay" data-testid="footer-phone-2"><Phone className="w-4 h-4"/> 021 023 3319</a>
            <a href="tel:0723994711" className="flex gap-2 items-center hover:text-clay" data-testid="footer-phone-cell"><Phone className="w-4 h-4"/> 072 399 4711</a>
            <a href="https://wa.me/27723994711" target="_blank" rel="noreferrer" className="flex gap-2 items-center hover:text-clay" data-testid="footer-whatsapp-louw">WhatsApp Dr Louw</a>
            <a href="https://wa.me/27689569622" target="_blank" rel="noreferrer" className="flex gap-2 items-center hover:text-clay" data-testid="footer-whatsapp-vaneck">WhatsApp Dr Van Eck</a>
          </div>
        </div>

        <div>
          <div className="eyebrow text-sage">Email</div>
          <div className="mt-3 space-y-2 text-sm">
            <a href="mailto:duprelouw@gmail.com" className="flex gap-2 items-center hover:text-clay" data-testid="footer-email-general"><Mail className="w-4 h-4"/> duprelouw@gmail.com</a>
            <a href="mailto:script.oakmed@gmail.com" className="flex gap-2 items-center hover:text-clay" data-testid="footer-email-scripts"><Mail className="w-4 h-4"/> script.oakmed@gmail.com</a>
            <a href="mailto:dlouw.unicare@gmail.com" className="flex gap-2 items-center hover:text-clay" data-testid="footer-email-unicare"><Mail className="w-4 h-4"/> dlouw.unicare@gmail.com</a>
          </div>
          <div className="mt-6">
            <div className="eyebrow text-sage">After-hours</div>
            <p className="text-sm mt-2">Uniclinic Latecare<br/><a href="tel:0675923217" className="text-clay" data-testid="footer-aftercare-phone">067 592 3217</a></p>
          </div>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row gap-2 justify-between text-xs text-cream/50">
          <span>© {new Date().getFullYear()} Dr Du Pré Louw Family Practice. All rights reserved.</span>
          <span><a href="/login" className="hover:text-clay" data-testid="admin-login-link">Admin Login</a></span>
        </div>
      </div>
    </footer>
  );
}
